/**
 * Generic address entry field used for adding a new address
 * Wrap all the address entry fields in a <div class="row"/> when using the grid
 * - Address fields are generic because they vary based on the user's locale
 * - Shows a state list dropdown if it exists
 * - Validates the postcode (zipcode) and shows the error message (vx_form-control-message)
 * - If it's a US address, show the state and zipcode on the same line
 */

import React from 'react';
import classNames from 'classnames';
import { StyleSheet, css } from 'aphrodite';
import TextInput from 'react-text-input';
import PropTypes from 'prop-types';
import { stateListPropType } from 'prop-validators';

const STYLES = StyleSheet.create({
	dropdown: {
		padding: 0,
		textIndent: 12,
		boxSizing: 'content-box',
		WebkitAppearance: 'none',
		MozAppearance: 'none',
		appearance: 'none',
		'@-moz-document url-prefix()': {
			textIndent: 2.5,
		},
		'::-ms-expand': { display: 'none' },
	},

	errorMessage: {
		WebkitTransition: 'all 0.25s ease-in-out',
		MozTransition: 'all 0.25s ease-in-out',
		msTransition: 'all 0.25s ease-in-out',
		OTransition: 'all 0.25s ease-in-out',
		transition: 'all 0.25s ease-in-out',
		color: 'rgb(0, 0, 0)'
	},
	arrow: {
		position: 'absolute',
		right: 0,
		top: '50%',
		cursor: 'default',
		pointerEvents: 'none',
	}
});

/**
 * State dropdown list
 */
class StateList extends React.Component {
	static propTypes = {
		id: PropTypes.string.isRequired,
		country: PropTypes.string.isRequired,
		fieldName: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		stateList: stateListPropType,
		required: PropTypes.bool,
		value: PropTypes.string, // default value
		errorMessage: PropTypes.string,
		handleChangeField: PropTypes.func.isRequired,
		handleFocus: PropTypes.func,
		handleBlur: PropTypes.func,
	};

	state = {
		value: this.props.value || ''
	};

	handleChangeOption = (event) => {
		let value = event.target.value;
		this.setState({ value });
		this.props.handleChangeField(this.props.fieldName, value);
	};

	render() {
		let { id, fieldName, required, label, stateList = [], errorMessage } = this.props;

		let errorMessageClassNames = classNames(css(STYLES.errorMessage), {
			'vx_form-control-message': errorMessage
		});

		let errorStylesSelect = {}

		let errorStyles = {
			height: 0,
			padding: 0
		};

		if (errorMessage) {
			errorStyles = { height: '100%', marginRight: -2 };
			errorStylesSelect = { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 };
		}

		return (
			<div className={classNames({
				'vx_has-error-with-message vx_has-error': errorMessage,
				'col-sm-12': this.props.country !== 'US',
				'col-sm-8': this.props.country === 'US',
			})} style={{ margin: '10px 0 0', position: 'relative' }}>
				<div style={{ position: 'relative', marginTop: 0 }}>
					<select name={fieldName} id={id}
						className={`vx_form-control vx_form-control_complex' ${css(STYLES.dropdown)}`}
						required={required}
						value={this.state.value}
						onChange={this.handleChangeOption}
						onFocus={this.props.handleFocus}
						style={errorStylesSelect}>
						<option value="" disabled>{label}</option>
							{stateList.map(({ $id: code, $elt: value }) => (
						<option key={code} value={code}>{value}</option>
					))}
					</select>
					{!errorMessage && <label htmlFor={fieldName} className={`icon icon-arrow-down-small ${css(STYLES.arrow)}`}></label>}

					<span className="vx_selectIcon_error"></span>

					{/* show the error message if it exists */}
					<div className={errorMessageClassNames} style={errorStyles}>{errorMessage}</div>
				</div>
			</div>
		)
	}
}

/**
* A generic address entry field that handles different fields specified by griffin
* Values will be stored in component state
*/
class AddressEntryField extends React.Component {
	static propTypes = {
		id: PropTypes.string.isRequired,
		country: PropTypes.string.isRequired,
		fieldName: PropTypes.string.isRequired,
		required: PropTypes.bool,
		maxLength: PropTypes.number,
		validationPattern: PropTypes.string,
		labels: PropTypes.object.isRequired, // addressEntryFormat.strings
		value: PropTypes.string, // default value
		errorMessage: PropTypes.string,
		handleChangeField: PropTypes.func.isRequired,
		handleFocus: PropTypes.func,
		handleBlur: PropTypes.func,
		handlePostcodeHasError: PropTypes.func.isRequired
	};

	static defaultProps = {
		handleFocus: () => {},
		handleBlur: () => {}
	};

	state = {
		value: '',
		zipErrorMessage: ''
	};

	handleFocus = (value) => {
		this.props.handleFocus(this.props.fieldName, value);
	};

	/**
	* Validate postcode onBlur
	* - set the state with the error message from the labels object
	* - call parent's handlePostcodeHasError to disable submission of the form
	*/
	handleBlur = (value) => {
		let shouldValidatePostcode = this.props.fieldName === 'postcode' && this.state.value;

		// if the postcode is invalid based on the validationPattern,
		// set zipErrorMessage = labels.validationError that we get from Griffin
		if (shouldValidatePostcode && !(new RegExp(`^${this.props.validationPattern}$`).test(this.state.value))) {
			this.setState({ zipErrorMessage: this.props.labels.validationError });
			this.props.handlePostcodeHasError(true);
		} else {
			this.setState({ zipErrorMessage: '' });
			this.props.handlePostcodeHasError(false);
		}

		this.props.handleBlur(this.props.fieldName, value);
	};

	/**
	 * Handle changing the input value
	 * - call parent's handleChangeField to store the address value
	 * - set the state so the component also keeps track of this value
	 * @param {String} value - the input value
	 */
	handleChangeField = (value) => {
		this.props.handleChangeField(this.props.fieldName, value);
		this.setState({ value });
		return value;
	};

	render() {
		let { id, country, fieldName, required, maxLength, validationPattern, labels, errorMessage, handleChangeField } = this.props;
		let { optionalLabel, stateList } = labels;
		optionalLabel = required ? '' : ` ${optionalLabel || ''}`;
		let label = `${labels[`${fieldName}Label`]}${optionalLabel}`;
		let attributes = {
			required,
			maxlength: maxLength,
			pattern: validationPattern
		};

		// if there is a stateList, let's render a <StateList/> dropdown
		if (this.props.fieldName === 'state' && stateList && stateList.length) {

			return (
				<StateList
					id={id}
					country={country}
					fieldName={fieldName}
					label={label}
					value={this.props.value}
					{...attributes}
					stateList={stateList}
					errorMessage={errorMessage}
					handleChangeField={handleChangeField}
					handleFocus={this.handleFocus} />
			);
		}

		let { zipErrorMessage } = this.state;
		let errorMessageClassNames = classNames(css(STYLES.errorMessage), {
			'vx_form-control-message': errorMessage || zipErrorMessage
		});
		let errorStyles = {
			height: 0,
			padding: 0
		};
		if (errorMessage || zipErrorMessage) {
			errorStyles = { height: '100%' };
		}

		return (
			<div className={classNames({
				'col-sm-12': this.props.country !== 'US' || fieldName !== 'postcode',
				'col-sm-4': this.props.country === 'US' && fieldName === 'postcode',
				'vx_has-error-with-message': errorMessage || zipErrorMessage
			})} style={{ margin: '10px 0 0' }}>
				<TextInput
					id={id}
					name={`${fieldName}`}
					className="vx_form-control vx_form-control_complex"
					label={label}
					value={this.props.value}
					{...attributes}
					handleFocus={this.handleFocus}
					handleBlur={this.handleBlur}
					handleChange={this.handleChangeField} />

				{/* show the error message if it exists */}
				<div className={errorMessageClassNames} style={errorStyles}>{errorMessage || zipErrorMessage}</div>
				</div>
		);
	}
}

export default AddressEntryField;



// WEBPACK FOOTER //
// ./components/address-entry-field.js